import { useTranslation } from '../design-system/data/translations';

interface CircularProgressProps {
  readonly value: number;
  readonly size?: number;
  readonly strokeWidth?: number;
  readonly label?: string;
}

/**
 * Jauge circulaire avec typographie proportionnelle à la taille.
 * La valeur et l'unité (%) sont dimensionnées dynamiquement pour rester
 * lisibles et centrées quelle que soit la taille de l'anneau.
 */
export function CircularProgress({
  value,
  size = 144,
  strokeWidth = 7,
  label,
}: CircularProgressProps) {
  const { t } = useTranslation();
  const clamped = Math.min(100, Math.max(0, value));
  const radius = size / 2 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - clamped / 100);

  // Typographie proportionnelle à la taille de la jauge.
  // La valeur suit ~38% du diamètre, bornée pour rester lisible.
  const valueFontSize = Math.max(13, Math.min(44, Math.round(size * 0.38)));
  const unitFontSize = Math.max(6, Math.round(valueFontSize * 0.44));

  return (
    <div
      className="circular-progress"
      style={{ width: size, height: size }}
      role="progressbar"
      aria-label={label ?? t('progress.aria').replace('{percent}', String(clamped))}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          className="circular-progress__track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          className="circular-progress__arc"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="circular-progress__label" aria-hidden="true">
        <span className="circular-progress__value" style={{ fontSize: valueFontSize }}>
          {clamped}
        </span>
        <span className="circular-progress__unit" style={{ fontSize: unitFontSize }}>
          %
        </span>
      </span>
    </div>
  );
}
